import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignListDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * The campaign list, and sending one.
 *
 * Sending is the only irreversible thing in this product: once rows are on the
 * queue a worker starts delivering them, and there is no recall. So the button
 * is disabled while the request is in flight, and every answer the server can
 * give is reported rather than collapsed into success.
 *
 * The list is reloaded afterwards rather than patched in place. What changed is
 * the status *and* the audience size, and the audience size is a number only the
 * server knows — guessing it here would show someone a count that was never
 * true.
 */

export type CampaignsState = AsyncState<CampaignListDto>

export interface Notice {
  readonly tone: 'ok' | 'danger'
  readonly text: string
}

export function useCampaigns({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.campaigns(), [client])
  const { state, reload } = useLoaded<CampaignListDto>(
    load,
    'We could not load your campaigns. Please try again in a moment.',
  )
  const [notice, setNotice] = useState<Notice | undefined>(undefined)

  /**
   * Deletes a draft.
   *
   * Only a draft can go: the send log and the queue cascade from the campaign
   * row, so deleting one that has been sent would erase the record of what
   * went out. The server refuses, and the refusal *is* the message.
   */
  const remove = useCallback(
    async (campaignId: string) => {
      setNotice(undefined)
      try {
        const outcome = await client.deleteCampaign(campaignId)
        if (outcome === 'sent') {
          setNotice({
            tone: 'danger',
            text: 'That campaign has been sent. Deleting it would erase the record of what went out and to whom.',
          })
          return
        }
        await reload()
      } catch {
        setNotice({
          tone: 'danger',
          text: 'We could not delete that campaign. Please try again in a moment.',
        })
      }
    },
    [client, reload],
  )

  return { state, notice, remove, retry: reload }
}
